import React from 'react';
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { motion } from "framer-motion";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { Button } from "@/components/ui/button";
import { MessageCircle, Gift, Sparkles, TrendingDown, FileText, ArrowRight, Clock } from "lucide-react";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { schoolPrices } from "@/config/schoolPrices";
import { Link } from "react-router-dom";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Tarifas y horarios | Gándara International School",
    description: "Información sobre precios, horarios y servicios incluidos en Gándara International School. Educación internacional desde 450€/mes.",
  },
  en: {
    title: "Fees & Schedule | Gándara International School",
    description: "Information about tuition fees, schedules and services at Gándara International School. International education from €450/month.",
  },
  pt: {
    title: "Tarifas e Horários | Gándara International School",
    description: "Informação sobre preços, horários e serviços incluídos na Gándara International School. Educação internacional desde 450€/mês.",
  },
  fr: {
    title: "Tarifs et Horaires | Gándara International School",
    description: "Informations sur les tarifs, horaires et services inclus à Gándara International School. Éducation internationale à partir de 450€/mois.",
  },
};

const Prices = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner />
      
      <div className="container mx-auto px-4 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-16">
            <h1 className="text-5xl font-light text-primary mb-6">
              {t.prices.title}
            </h1>
            <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">
              {language === 'es' ? 'Inversión en la educación y el futuro de tus hijos' :
               language === 'pt' ? 'Investimento na educação e no futuro dos seus filhos' :
               language === 'fr' ? 'Investissement dans l\'éducation et l\'avenir de vos enfants' :
               'Investment in your children\'s education and future'}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-xl"
              >
                <Link
                  to={`/${language}/${language === 'es' ? 'admisiones' : language === 'en' ? 'admissions' : language === 'pt' ? 'admissoes' : 'admissions'}`}
                >
                  <FileText className="w-5 h-5" />
                  {t.prices.preEnrollment.ctaPrimary}
                </Link>
              </Button>
              <Button
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
                variant="outline"
                size="lg"
                className="rounded-lg border-2 transform transition-all duration-200 hover:scale-105"
              >
                <MessageCircle className="w-5 h-5" />
                {t.prices.preEnrollment.ctaSecondary}
              </Button>
            </div>
          </div>

          {/* Monthly Fees Section with Table */}
          <section className="mb-20">
            <h2 className="text-3xl font-light mb-4 text-primary text-center">{t.prices.monthlyFees.title}</h2>
            <p className="text-center text-muted-foreground mb-8">{t.prices.monthlyFees.subtitle}</p>
            
            <div className="bg-card backdrop-blur-sm rounded-2xl shadow-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-primary/5 border-b">
                      <th className="text-left p-6 font-medium text-primary">{t.prices.monthlyFees.tableHeaders.level}</th>
                      <th className="text-center p-6 font-medium text-primary">{t.prices.monthlyFees.tableHeaders.firstChild}</th>
                      <th className="text-center p-6 font-medium text-primary">{t.prices.monthlyFees.tableHeaders.secondChild}</th>
                      <th className="text-center p-6 font-medium text-primary">{t.prices.monthlyFees.tableHeaders.thirdChild}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Infantil Row */}
                    <tr className="border-b hover:bg-accent/5 transition-colors">
                      <td className="p-6">
                        <div>
                          <p className="font-medium text-lg">{t.prices.monthlyFees.infantil.title}</p>
                          <p className="text-sm text-muted-foreground">{t.prices.monthlyFees.infantil.ageRange}</p>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <p className="text-2xl font-medium text-primary">{schoolPrices.monthlyFees.infantil.price}</p>
                      </td>
                      <td className="p-6 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="inline-block px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-xs font-medium mb-1">-20%</span>
                          <p className="text-2xl font-medium text-green-600 dark:text-green-400">{schoolPrices.monthlyFees.infantil.discounted.second}</p>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="inline-block px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-xs font-medium mb-1">-40%</span>
                          <p className="text-2xl font-medium text-green-600 dark:text-green-400">{schoolPrices.monthlyFees.infantil.discounted.third}</p>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Primary C1 Row */}
                    <tr className="border-b hover:bg-accent/5 transition-colors">
                      <td className="p-6">
                        <div>
                          <p className="font-medium text-lg">{t.prices.monthlyFees.primaryC1.title}</p>
                          <p className="text-sm text-muted-foreground">{t.prices.monthlyFees.primaryC1.ageRange}</p>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <p className="text-2xl font-medium text-primary">{schoolPrices.monthlyFees.primaryC1.price}</p>
                      </td>
                      <td className="p-6 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="inline-block px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-xs font-medium mb-1">-20%</span>
                          <p className="text-2xl font-medium text-green-600 dark:text-green-400">{schoolPrices.monthlyFees.primaryC1.discounted.second}</p>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="inline-block px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-xs font-medium mb-1">-40%</span>
                          <p className="text-2xl font-medium text-green-600 dark:text-green-400">{schoolPrices.monthlyFees.primaryC1.discounted.third}</p>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Primary C2 Row */}
                    <tr className="hover:bg-accent/5 transition-colors">
                      <td className="p-6">
                        <div>
                          <p className="font-medium text-lg">{t.prices.monthlyFees.primaryC2.title}</p>
                          <p className="text-sm text-muted-foreground">{t.prices.monthlyFees.primaryC2.ageRange}</p>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <p className="text-2xl font-medium text-primary">{schoolPrices.monthlyFees.primaryC2.price}</p>
                      </td>
                      <td className="p-6 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="inline-block px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-xs font-medium mb-1">-20%</span>
                          <p className="text-2xl font-medium text-green-600 dark:text-green-400">{schoolPrices.monthlyFees.primaryC2.discounted.second}</p>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="inline-block px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-xs font-medium mb-1">-40%</span>
                          <p className="text-2xl font-medium text-green-600 dark:text-green-400">{schoolPrices.monthlyFees.primaryC2.discounted.third}</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Special Discounts Section */}
          <section className="mb-20">
            <div className="flex items-center justify-center gap-3 mb-8">
              <Sparkles className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-light text-primary text-center">{t.prices.specialDiscounts.title}</h2>
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Early Enrollment Discount */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-gradient-to-br from-accent/10 to-accent/5 p-8 rounded-2xl shadow-lg border-2 border-accent/20"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-accent/20 rounded-full">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-primary mb-2">{t.prices.specialDiscounts.earlyEnrollment.title}</h3>
                    <span className="inline-block px-3 py-1 bg-accent text-accent-foreground rounded-full text-sm font-medium">
                      {t.prices.specialDiscounts.earlyEnrollment.badge}
                    </span>
                  </div>
                </div>
                <p className="text-muted-foreground">{t.prices.specialDiscounts.earlyEnrollment.description}</p>
              </motion.div>

              {/* Annual Payment Discount */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-gradient-to-br from-primary/10 to-primary/5 p-8 rounded-2xl shadow-lg border-2 border-primary/20"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-primary/20 rounded-full">
                    <Gift className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-primary mb-2">{t.prices.specialDiscounts.annualPayment.title}</h3>
                    <div className="flex gap-2">
                      <span className="inline-block px-3 py-1 bg-primary text-white rounded-full text-sm font-medium">
                        {t.prices.specialDiscounts.annualPayment.badges.discount}
                      </span>
                      <span className="inline-block px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium">
                        {t.prices.specialDiscounts.annualPayment.badges.voucher}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-muted-foreground">{t.prices.specialDiscounts.annualPayment.description}</p>
              </motion.div>
            </div>
          </section>

          {/* Enrollment Section - Restructured */}
          <section className="mb-20">
            <h2 className="text-3xl font-light mb-4 text-primary text-center">{t.prices.enrollment.title}</h2>
            <p className="text-center text-muted-foreground mb-12">{t.prices.enrollment.description}</p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Renewal */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-card p-8 rounded-2xl shadow-lg border"
              >
                <div className="bg-primary/10 -mx-8 -mt-8 mb-6 p-6 rounded-t-2xl">
                  <h3 className="text-2xl font-medium text-primary">{t.prices.enrollment.renewal.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{t.prices.enrollment.renewal.subtitle}</p>
                </div>
                <div className="text-center mb-4">
                  <p className="text-5xl font-medium text-primary mb-2">{schoolPrices.enrollment.renewal}</p>
                  <p className="text-sm text-muted-foreground italic">{t.prices.enrollment.renewal.note}</p>
                </div>
              </motion.div>

              {/* New Students */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-card p-8 rounded-2xl shadow-lg border"
              >
                <div className="bg-accent/10 -mx-8 -mt-8 mb-6 p-6 rounded-t-2xl">
                  <h3 className="text-2xl font-medium text-primary">{t.prices.enrollment.newStudents.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{t.prices.enrollment.newStudents.subtitle}</p>
                </div>
                <ul className="space-y-4">
                  <li>
                    <p className="font-medium">{t.prices.enrollment.newStudents.firstChild.title}</p>
                    <p className="text-3xl text-primary">{schoolPrices.enrollment.newStudents.firstChild}</p>
                  </li>
                  <li>
                    <p className="font-medium">{t.prices.enrollment.newStudents.siblings.title}</p>
                    <p className="text-3xl text-primary">{schoolPrices.enrollment.newStudents.siblings}</p>
                  </li>
                </ul>
              </motion.div>
            </div>
          </section>

          {/* Discounts Info Section */}
          <section className="mb-20">
            <div className="flex items-center justify-center gap-3 mb-8">
              <TrendingDown className="w-8 h-8 text-green-500" />
              <h2 className="text-3xl font-light text-primary text-center">{t.prices.discounts.title}</h2>
            </div>
            <p className="text-center text-muted-foreground mb-8">{t.prices.discounts.description}</p>
            <div className="bg-card p-8 rounded-xl shadow-lg border">
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="px-3 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-sm font-medium">-20%</div>
                  <div>
                    <p className="font-medium">{t.prices.discounts.secondChild.title}</p>
                    <p className="text-muted-foreground">{t.prices.discounts.secondChild.description}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="px-3 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-sm font-medium">-40%</div>
                  <div>
                    <p className="font-medium">{t.prices.discounts.thirdChild.title}</p>
                    <p className="text-muted-foreground">{t.prices.discounts.thirdChild.description}</p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Additional Services Section */}
          <section>
            <h2 className="text-3xl font-light mb-12 text-primary text-center">{t.prices.additionalServices.title}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Transport */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-card p-8 rounded-xl shadow-lg border"
              >
                <h3 className="text-xl font-medium mb-4">{t.prices.additionalServices.transport.title}</h3>
                <p className="mb-4 text-sm text-muted-foreground">{t.prices.additionalServices.transport.description}</p>
                <ul className="space-y-2">
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.transport.roundTrip.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.transport.roundTrip}</p>
                  </li>
                </ul>
              </motion.div>

              {/* Early Birds */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-card p-8 rounded-xl shadow-lg border"
              >
                <h3 className="text-xl font-medium mb-4">{t.prices.additionalServices.earlyBirds.title}</h3>
                <ul className="space-y-2">
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.earlyBirds.monthly.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.earlyBirds.monthly}</p>
                  </li>
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.earlyBirds.daily.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.earlyBirds.daily}</p>
                  </li>
                </ul>
                <p className="mt-4 text-sm text-muted-foreground">{t.prices.additionalServices.earlyBirds.description}</p>
                <p className="mt-2 text-sm font-medium text-primary">
                  {language === 'es' ? '* Precio por familia' : 
                   language === 'pt' ? '* Preço por família' : 
                   language === 'fr' ? '* Prix par famille' : 
                   '* Price per family'}
                </p>
              </motion.div>

              {/* Food */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-card p-8 rounded-xl shadow-lg border"
              >
                <h3 className="text-xl font-medium mb-4">{t.prices.additionalServices.food.title}</h3>
                <ul className="space-y-2">
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.food.monthly.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.food.monthly}{t.services.lunch.perMonth}</p>
                  </li>
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.food.threeDays.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.food.threeDays}{t.services.lunch.perMonth}</p>
                  </li>
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.food.twoDays.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.food.twoDays}{t.services.lunch.perMonth}</p>
                  </li>
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.food.daily.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.food.daily}</p>
                  </li>
                </ul>
                <p className="mt-4 text-sm text-muted-foreground">{t.prices.additionalServices.food.description}</p>
                
                {/* Takeaway subsection */}
                <div className="mt-6 pt-4 border-t">
                  <h4 className="font-medium text-primary/90 mb-2">{t.prices.additionalServices.food.takeaway.title}</h4>
                  <p className="text-sm text-muted-foreground">{t.prices.additionalServices.food.takeaway.description}</p>
                </div>
              </motion.div>

              {/* Ludoteca */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-card p-8 rounded-xl shadow-lg border"
              >
                <h3 className="text-xl font-medium mb-4">{t.prices.additionalServices.ludoteca.title}</h3>
                <p className="mb-4 text-sm text-muted-foreground">{t.prices.additionalServices.ludoteca.schedule}</p>
                <div className="mb-4 p-3 bg-accent/10 rounded-lg">
                  <p className="text-sm text-accent-foreground font-medium">{t.prices.additionalServices.ludoteca.freeService}</p>
                </div>
                <ul className="space-y-2">
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.ludoteca.dayPass.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.ludoteca.dayPass}/día</p>
                  </li>
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.ludoteca.oneDay.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.ludoteca.oneDay}/mes</p>
                  </li>
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.ludoteca.twoDays.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.ludoteca.twoDays}/mes</p>
                  </li>
                  <li>
                    <p className="font-medium">{t.prices.additionalServices.ludoteca.fiveDays.title}</p>
                    <p className="text-primary">{schoolPrices.additionalServices.ludoteca.fiveDays}/mes</p>
                  </li>
                </ul>
                <p className="mt-4 text-sm text-muted-foreground">{t.prices.additionalServices.ludoteca.description}</p>
                <p className="mt-2 text-sm font-medium text-primary">
                  {language === 'es' ? '* Precio por familia' : 
                   language === 'pt' ? '* Preço por família' : 
                   language === 'fr' ? '* Prix par famille' : 
                   '* Price per family'}
                </p>
              </motion.div>
            </div>
          </section>

          {/* Pre-enrollment CTA Section */}
          <section className="mt-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 rounded-3xl p-12 border-2 border-primary/20 shadow-2xl"
            >
              <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
              
              <div className="relative z-10 text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 mb-6">
                  <Sparkles className="w-8 h-8 text-primary animate-pulse" />
                  <h2 className="text-4xl md:text-5xl font-light text-primary">
                    {t.prices.preEnrollment.title}
                  </h2>
                  <Sparkles className="w-8 h-8 text-primary animate-pulse" />
                </div>
                
                <p className="text-xl text-muted-foreground mb-6">
                  {t.prices.preEnrollment.subtitle}
                </p>
                
                <div className="flex items-center justify-center gap-4 mb-8 text-lg font-medium text-primary/80">
                  <span>{t.prices.preEnrollment.steps.split(' → ')[0]}</span>
                  <ArrowRight className="w-5 h-5 text-primary" />
                  <span>{t.prices.preEnrollment.steps.split(' → ')[1]}</span>
                  <ArrowRight className="w-5 h-5 text-primary" />
                  <span>{t.prices.preEnrollment.steps.split(' → ')[2]}</span>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
                  <Button
                    asChild
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-7 rounded-lg text-lg shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-2xl"
                  >
                    <Link
                      to={`/${language}/${language === 'es' ? 'admisiones' : language === 'en' ? 'admissions' : language === 'pt' ? 'admissoes' : 'admissions'}`}
                    >
                      <FileText className="w-5 h-5" />
                      {t.prices.preEnrollment.ctaPrimary}
                    </Link>
                  </Button>
                  
                  <Button
                    onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
                    variant="outline"
                    size="lg"
                    className="px-10 py-7 rounded-lg text-lg border-2 transform transition-all duration-200 hover:scale-105"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {t.prices.preEnrollment.ctaSecondary}
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground italic">
                  {t.prices.preEnrollment.note}
                </p>
              </div>
            </motion.div>
          </section>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default Prices;
